import { Elements } from "@/components/elements/Elements";
import { Element } from "@/components/elements/UIElement";
import { createContext } from "react";
import { ElementDetails, Profile, Team, User } from "./types";

export const UserContext = createContext<{
  user: User | null;
  syncUser: () => void;
  accessToken: string | null;
  setAccessToken: (token: string | null) => void;
  isAuthenticationModalOpen: boolean;
  setAuthenticationModalOpen: (value: boolean) => void;
  isUserModalOpen: boolean;
  setUserModalOpen: (value: boolean) => void;
  isCreateProfileModalOpen: boolean;
  setCreateProfileModalOpen: (value: boolean) => void;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  logout: () => void;
  showSubscriptionsModal: boolean;
  setShowSubscriptionsModal: (value: boolean) => void;
  unpaidSubscriptions: Team[];
}>({
  user: null,
  syncUser: () => {},
  accessToken: null,
  setAccessToken: () => {},
  isAuthenticationModalOpen: false,
  setAuthenticationModalOpen: () => {},
  isUserModalOpen: false,
  setUserModalOpen: () => {},
  isCreateProfileModalOpen: false,
  setCreateProfileModalOpen: () => {},
  isLoading: false,
  isError: false,
  error: null,
  logout: () => {},
  showSubscriptionsModal: false,
  setShowSubscriptionsModal: () => {},
  unpaidSubscriptions: [],
});

export const ElementsContext = createContext<{
  profiles: Profile[];
  activeProfile?: Profile;
  isSwitchingProfiles: boolean;
  syncProfiles: () => void;
  activeProfileId: string | null;
  setActiveProfileId: (value: string | null) => void;
  elements: Record<string, Element>;
  setElements: (elements: Record<string, Element>) => void;
  saveElements: (
    eles?: Record<string, Element>,
    options?: { ignoreSync?: boolean },
  ) => Promise<void>;
  appendElement: (
    elementId: string,
    elementName: keyof typeof Elements,
    targetId: string,
    config?: Record<string, unknown>,
    options?: {
      source?: "gallery_add_button" | "gallery_drag_drop" | "unknown";
    },
  ) => Promise<void>;
  updateElement: (
    elementId: string,
    config: Record<string, unknown>,
  ) => Promise<void>;
  removeElement: (elementId: string) => Promise<void>;
  isDragging: boolean;
  setIsDragging: (value: boolean) => void;
  showGreeting: boolean;
  setShowGreeting: (value: boolean) => void;
  isProfileOwned: boolean;
  isProfileShared: boolean;
  hasUnsyncedChanges: boolean;
  isLocked: boolean;
  setIsLocked: (value: boolean) => void;
}>({
  profiles: [],
  activeProfile: undefined,
  isSwitchingProfiles: false,
  syncProfiles: () => {},
  activeProfileId: null,
  setActiveProfileId: () => {},
  elements: {},
  setElements: () => {},
  saveElements: async () => {},
  appendElement: async () => {},
  removeElement: async () => {},
  updateElement: async () => {},
  isDragging: false,
  setIsDragging: () => {},
  showGreeting: false,
  setShowGreeting: () => {},
  isProfileOwned: false,
  isProfileShared: false,
  hasUnsyncedChanges: false,
  isLocked: false,
  setIsLocked: () => {},
});

export type ElementContextType<T, Selected = unknown> = {
  id: string;
  elementDetails: ElementDetails<T>;
  open: boolean;
  setOpen: (open: boolean, props?: Record<string, unknown>) => void;
  setupOpen: boolean;
  setSetupOpen: (open: boolean) => void;
  selected: Selected;
  setSelected: (selected: Selected) => void;
  config: ElementDetails<T>["defaultProps"] & {
    integrationIds: string[];
    size: keyof ElementDetails<T>["grid"];
  };
};

export const ElementContext = createContext<ElementContextType<any>>({
  id: "",
  elementDetails: {
    name: "",
    description: "",
    category: "",
    defaultProps: {},
    grid: {},
  },
  open: false,
  setOpen: () => {},
  setupOpen: false,
  setSetupOpen: () => {},
  selected: "",
  setSelected: () => {},
  config: { integrationIds: [], size: "" },
});

export const NewElementDndContext = createContext<{
  elementName: string;
  setElementName: (elementName: string) => void;
}>({
  elementName: "",
  setElementName: () => {},
});
